package com.swetdot.swetgram.ui.legal

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.auth.AuthRepository
import com.swetdot.swetgram.data.legal.LegalRepository
import com.swetdot.swetgram.data.profile.ProfileRepository
import com.swetdot.swetgram.protocol.dto.LegalStatus
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class LegalUiState(
    val loading: Boolean = true,
    val status: LegalStatus? = null,
    val busy: Boolean = false,
    val error: String? = null,
    val deletionScheduled: Boolean = false,
)

internal enum class LegalGateDestination {
    CHECKING,
    ACCEPTANCE,
    APP,
}

/**
 * Chooses a destination only from a status the server has actually returned.
 *
 * A missing status is not the same as a rejected acceptance. On a cold launch the state starts
 * with `status == null` while `GET /legal/status` is in flight; treating that as `accepted ==
 * false` briefly rendered the 16+ acceptance screen for users who had already accepted it.
 */
internal fun LegalUiState.gateDestination(): LegalGateDestination = when (status?.accepted) {
    true -> LegalGateDestination.APP
    false -> LegalGateDestination.ACCEPTANCE
    null -> LegalGateDestination.CHECKING
}

class LegalViewModel(
    private val legal: LegalRepository,
    private val auth: AuthRepository,
    private val profiles: ProfileRepository,
) : ViewModel() {
    private val _state = MutableStateFlow(LegalUiState())
    val state: StateFlow<LegalUiState> = _state.asStateFlow()

    init { refresh() }

    fun refresh() = launchBusy(initialLoad = true) { status -> status.copy(status = legal.status()) }

    fun accept() {
        val current = _state.value.status ?: return
        launchBusy { state -> state.copy(status = legal.accept(current)) }
    }

    fun signOut() {
        viewModelScope.launch { auth.logout() }
    }

    fun requestDeletion() = launchBusy { state ->
        profiles.scheduleDeletion()
        state.copy(deletionScheduled = true)
    }

    fun dismissError() { _state.value = _state.value.copy(error = null) }

    private fun launchBusy(
        initialLoad: Boolean = false,
        block: suspend (LegalUiState) -> LegalUiState,
    ) {
        _state.value = _state.value.copy(busy = true, loading = initialLoad, error = null)
        viewModelScope.launch {
            try {
                _state.value = block(_state.value).copy(loading = false)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                _state.value = _state.value.copy(
                    loading = false,
                    error = failure.message ?: "Could not contact Swetgram. Please try again.",
                )
            } finally {
                _state.value = _state.value.copy(busy = false)
            }
        }
    }
}
